import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
// @ts-ignore
import validator from 'validator';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser, setUserSession } from '@/lib/common';
import uploadFileToBlob, { getPhotoUploadUrl } from '@/lib/azure-blob';

const CircleUploadButton = styled(Box)(() => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  border: '1px dashed #C6CFDC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#7A8497',
  backgroundColor: '#FCFDFF',
  position: 'relative',
  overflow: 'hidden',
  transition: 'background-color .12s, border-color .12s',
  '&:hover': {
    backgroundColor: '#F2F7FD',
    borderColor: '#9FC2F0',
  },
}));

const roleConfig = [
  { key: 'is_technical', label: 'Technical' },
  { key: 'is_business', label: 'Business' },
  { key: 'is_sponsor', label: 'Sponsor' },
  { key: 'is_admin', label: 'Admin' },
] as const;

const PASSWORD_VALIDATION_ERROR =
  'Password must be at least 6 characters with lowercase, uppercase, number and special character';

function isPasswordValid(value: string): boolean {
  if (!value.trim()) return true;
  return validator.isStrongPassword(value, {
    minLength: 6,
    minLowercase: 1,
    minUppercase: 1,
    minNumbers: 1,
  });
}

type ProfileUser = {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  photo_path?: string | null;
  default_currency?: string | null;
  passwordChangeAllowed?: boolean;
  is_admin?: boolean;
  is_technical?: boolean;
  is_business?: boolean;
  is_sponsor?: boolean;
};

export default function MyProfile() {
  const sessionUser = getUser();
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [currenciesList, setCurrenciesList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(Math.random().toString(36));
  const [alertPassword, setAlertPassword] = useState(false);
  const [alertRepeatPassword, setAlertRepeatPassword] = useState(false);
  const [alertCurrency, setAlertCurrency] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      fetchWrapper.get(urls.User.current),
      fetchWrapper.get(urls.DevOps.currencies),
    ])
      .then(([userData, currencies]) => {
        if (cancelled) return;
        setProfile(userData);
        setCurrenciesList(currencies);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load profile');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile) return;
    setFileSelected(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setProfile({ ...profile, photo_path: file.name });
  };

  const handleSave = async () => {
    if (!profile?.id) return;

    if (!profile.default_currency) {
      setAlertCurrency(true);
      return;
    }
    setAlertCurrency(false);

    if (password && !isPasswordValid(password)) {
      setAlertPassword(true);
      return;
    }
    setAlertPassword(false);

    if (password && repeatPassword !== password) {
      setAlertRepeatPassword(true);
      return;
    }
    setAlertRepeatPassword(false);

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (fileSelected) {
        const uploaded = await uploadFileToBlob(fileSelected);
        if (uploaded?.url) {
          setProfile({ ...profile, photo_path: uploaded.url });
        }
      }

      let photoPath = profile.photo_path;
      if (photoPath) photoPath = await getPhotoUploadUrl(photoPath || '');

      const body: Record<string, unknown> = {
        photo_path: photoPath || '',
        default_currency: profile.default_currency,
      };
      if (password) {
        body.password = password;
      }

      await fetchWrapper.put(`${urls.DevOps.updateUserPassword}${profile.id}`, body);

      if (sessionUser && profile.default_currency) {
        setUserSession({
          ...sessionUser,
          default_currency: profile.default_currency,
        } as Parameters<typeof setUserSession>[0]);
      }

      setPassword('');
      setRepeatPassword('');
      setFileSelected(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setInputKey(Math.random().toString(36));

      const refreshed = await fetchWrapper.get(urls.User.current);
      setProfile(refreshed);
      setSuccess('Profile updated successfully.');
    } catch (err: any) {
      setError(err?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Alert severity="error">{error || 'Unable to load profile.'}</Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: 'center' }}>
              Photo
            </Typography>
            <CircleUploadButton component="label">
              {previewUrl || profile.photo_path ? (
                <Avatar
                  src={previewUrl || profile.photo_path || undefined}
                  sx={{ width: 116, height: 116 }}
                />
              ) : (
                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  Upload Photo
                </Typography>
              )}
              <input
                type="file"
                onChange={onFileChange}
                key={inputKey}
                hidden
                accept="image/*"
              />
            </CircleUploadButton>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: 360,
              mx: 'auto',
            }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: 'center', width: '100%' }}>
              Account details
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
              }}>
            <TextField
              label="First name"
              size="small"
              fullWidth
              value={profile.firstName ?? ''}
              disabled
            />
            <TextField
              label="Last name"
              size="small"
              fullWidth
              value={profile.lastName ?? ''}
              disabled
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={profile.email ?? ''}
              disabled
            />
            <TextField
              label="Username"
              size="small"
              fullWidth
              value={profile.username ?? ''}
              disabled
            />

            {profile?.passwordChangeAllowed && (
              <TextField
                label="Password (leave blank to keep current)"
                size="small"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={password}
                error={alertPassword}
                helperText={alertPassword ? PASSWORD_VALIDATION_ERROR : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setPassword(value);
                  setAlertPassword(value.trim() !== '' && !isPasswordValid(value));
                  if (value.trim() === '') {
                    setRepeatPassword('');
                    setAlertRepeatPassword(false);
                  } else {
                    setAlertRepeatPassword(
                      repeatPassword.trim() !== '' && repeatPassword !== value,
                    );
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label="toggle password visibility">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {profile?.passwordChangeAllowed && (
              <TextField
                label="Repeat password"
                size="small"
                type={showRepeatPassword ? 'text' : 'password'}
                fullWidth
                value={repeatPassword}
                disabled={password.trim() === ''}
                error={alertRepeatPassword}
                helperText={
                  alertRepeatPassword ? 'Passwords do not match' : ''
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setRepeatPassword(value);
                  setAlertRepeatPassword(value.trim() !== '' && value !== password);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowRepeatPassword(!showRepeatPassword)
                        }
                        edge="end"
                        aria-label="toggle repeat password visibility">
                        {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <Autocomplete
              disablePortal
              options={currenciesList}
              value={profile.default_currency ?? ''}
              getOptionLabel={(option: any) => option.currency ?? option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Default currency"
                  error={alertCurrency}
                  helperText={alertCurrency ? 'Default currency is required' : ''}
                />
              )}
              onChange={(_event, value) => {
                if (value) {
                  const currencyValue = value.currency ?? value;
                  setProfile({ ...profile, default_currency: currencyValue });
                  setAlertCurrency(false);
                }
              }}
            />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: 'center', width: '100%' }}>
              Roles
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content',
                  justifyItems: 'start',
                  rowGap: 0,
                }}>
                {roleConfig.map((role) => (
                  <FormControlLabel
                    key={role.key}
                    sx={{ m: 0, justifyContent: 'flex-start' }}
                    control={
                      <Checkbox
                        checked={Boolean(profile[role.key])}
                        disabled
                        size="small"
                      />
                    }
                    label={role.label}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : undefined}>
          Save changes
        </Button>
      </Box>
    </Box>
  );
}
